import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  IconButton,
  Popover,
  MenuItem,
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  FormHelperText,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { RxCross1 } from "react-icons/rx";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import "./AuditLogs.scss";

const AuditLogs = () => {
  const { setShowLoginPopup } = useAuth();
  const navigate = useNavigate();
  const httpService = new HttpService(setShowLoginPopup);

  const handleCloseModal = () => {
    navigate("/");
  };

  // Authorization barrier state (persisted via localStorage if rememberMe was selected)
  const [isAuthorized, setIsAuthorized] = useState(() => {
    return localStorage.getItem("audit_log_authorized") === "true";
  });
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [isEmailClicked, setIsEmailClicked] = useState(false);
  const [isPassClicked, setIsPassClicked] = useState(false);

  const handleAuditLoginSubmit = (e) => {
    e.preventDefault();
    setEmailErr("");
    setPasswordErr("");

    const targetEmail = "auditadmin";
    const targetPassword = "AuditAdmin@123";

    let hasError = false;
    const normalizedEmail = loginEmail.trim().toLowerCase();

    if (!loginEmail) {
      setEmailErr("Email is required");
      hasError = true;
    } else if (normalizedEmail !== targetEmail) {
      setEmailErr("Invalid Email/Username");
      hasError = true;
    }

    if (!loginPassword) {
      setPasswordErr("Password is required");
      hasError = true;
    } else if (loginPassword !== targetPassword) {
      setPasswordErr("Invalid Password");
      hasError = true;
    }

    if (hasError) return;

    if (rememberMe) {
      localStorage.setItem("audit_log_authorized", "true");
    }
    setIsAuthorized(true);
    toast.success("Authorized successfully");
  };

  const [logs, setLogs] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [loading, setLoading] = useState(false);

  // Pagination, Sort & Search States (Server Mode)
  const [page, setPage] = useState(0); // 0-based for DataGrid
  const [pageSize, setPageSize] = useState(15);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [harvestDate, setHarvestDate] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Popover State (Action Menu)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Details Modal State
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Fetch all import tags to build the ID -> Tag code map
  const fetchTags = async () => {
    try {
      const res = await httpService.get("harvestimport");
      const list = res?.data?.data || res?.data || [];
      const mapping = {};
      list.forEach((t) => {
        if (t.id && t.tag) {
          mapping[t.id] = t.tag;
        }
      });
      setTagMap(mapping);
    } catch (e) {
      console.error("Error fetching tags:", e);
    }
  };

  // Fetch audit logs from backend using server-side parameters
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const apiPage = page + 1; // Backend is 1-based page index
      const params = {
        page: apiPage,
        pageSize: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder.toUpperCase(),
      };

      if (search.trim() !== "") {
        params.search = search;
      }

      if (harvestDate) {
        params.harvestDate = harvestDate.format("YYYY-MM-DD");
      }

      const res = await httpService.get("audit-logs", params);
      const dataObj = res?.data || res || {};
      const list = dataObj.data || [];
      const pagination = dataObj.pagination || {};

      setLogs(list);
      setRowCount(pagination.totalRecords || list.length || 0);
    } catch (e) {
      console.error("Error fetching audit logs:", e);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchTags();
    }
  }, [isAuthorized]);

  // Re-fetch logs when pagination, search, dates or sorting changes
  useEffect(() => {
    if (isAuthorized) {
      fetchLogs();
    }
  }, [page, pageSize, search, harvestDate, sortBy, sortOrder, isAuthorized]);

  // Helper to resolve tag code from ID
  const getTagCode = (tagVal) => {
    if (!tagVal) return "-";
    if (typeof tagVal === "string" && tagVal.length > 10) return tagVal;
    const mapped = tagMap[tagVal];
    return mapped ? String(mapped) : String(tagVal);
  };

  // Action type helper
  const getActionType = (action) => {
    if (!action) return "-";
    const act = action.toUpperCase();
    // if (act === "UPDATE") return "Update Package";
    // if (act === "INSERT" || act === "CREATE") return "Add Note";
    // if (act === "MOVED") return "Move Package";
    // if (act === "DELETE") return "Delete Package";
    return action;
  };

  // Helper to parse arrays like selected_matric_ids or child_packages and return Metrc codes
  const parseSpecialKeys = (key, val) => {
    if (!val) return "-";
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map((item) => {
        if (!item) return "-";
        if (typeof item === "object") {
          const codeVal =
            item.tag ||
            item.child_package_tag ||
            item.matric_code ||
            item.matric_id ||
            item.id;
          return getTagCode(codeVal);
        }
        return getTagCode(item);
      })
      .join(", ");
  };

  // Helper to render changed fields in a 3-line horizontal grid layout inside table cells
  const renderValues = (valObj, otherObj, action = "", isOldColumn = false) => {
    const act = String(action || "").toUpperCase();
    const isMoveAction = act === "MOVED" || act === "MOVE";

    if (!valObj && !isMoveAction) return "-";

    const valSource = valObj || {};
    const otherSource = otherObj || {};

    const allKeys = new Set([
      ...Object.keys(valSource),
      ...Object.keys(otherSource),
    ]);

    const items = [];
    allKeys.forEach((key) => {
      if (key === "id" || key === "created_at" || key === "updated_at") return;

      // Special rule: When the action is moved, show move_to and moved_at in old value column only
      if (key === "move_to" || key === "moved_at") {
        if (isMoveAction) {
          if (!isOldColumn) return;
          const displayVal =
            valSource[key] !== undefined && valSource[key] !== null
              ? valSource[key]
              : otherSource[key];
          if (
            displayVal !== undefined &&
            displayVal !== null &&
            displayVal !== ""
          ) {
            const display = formatDateValue(displayVal, key);
            const label = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            items.push(
              <span
                key={key}
                style={{ marginRight: "16px", display: "inline-block" }}
              >
                <strong>{label}:</strong> {display}
              </span>,
            );
          }
          return;
        }
      }

      if (!valObj) return;

      const thisVal = valSource[key];
      const otherVal = otherSource[key];
      const isChanged = JSON.stringify(thisVal) !== JSON.stringify(otherVal);

      if (isChanged && (thisVal !== undefined || otherVal !== undefined)) {
        let display = "-";
        if (key === "selected_matric_ids" || key === "child_packages") {
          display = parseSpecialKeys(key, thisVal);
        } else if (
          key === "source_package_tag" ||
          key === "child_package_tag"
        ) {
          display = getTagCode(thisVal);
        } else {
          display = formatDateValue(thisVal, key);
        }

        let unit = "";
        if (key === "weight" || key === "waste") {
          unit = " lbs";
        }

        let label = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        if (key === "selected_matric_ids") {
          label = "Selected Matric IDs";
        } else if (key === "child_packages") {
          label = "Child Packages";
        }

        items.push(
          <span
            key={key}
            style={{ marginRight: "16px", display: "inline-block" }}
          >
            <strong>{label}:</strong> {display}
            {unit}
          </span>,
        );
      }
    });

    if (items.length === 0) return "-";

    const lines = [];
    const chunkSize = 2;
    for (let i = 0; i < items.length; i += chunkSize) {
      const chunk = items.slice(i, i + chunkSize);
      lines.push(
        <div
          key={i}
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            marginTop: i > 0 ? "2px" : "0",
          }}
        >
          {chunk}
        </div>,
      );
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "6px 0",
          lineHeight: "1.5",
          width: "100%",
        }}
      >
        {lines}
      </div>
    );
  };

  // Helper to format date strings to MM-DD-YYYY
  const formatDateValue = (val, key = "") => {
    if (val === null || val === undefined) return "-";
    const valStr = String(val);
    const isDateKey =
      key.toLowerCase().includes("date") || key.toLowerCase().endsWith("_at");
    const isDatePattern =
      /^\d{4}-\d{2}-\d{2}$/.test(valStr) ||
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(valStr);
    if (isDateKey || isDatePattern) {
      const d = dayjs(val);
      if (d.isValid()) {
        return d.format("MM-DD-YYYY hh:mm A");
      }
    }
    return valStr;
  };

  // Helper to format dates recursively inside JSON objects for raw views
  const formatDatesInObject = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const copy = Array.isArray(obj) ? [...obj] : { ...obj };
    for (const key in copy) {
      const val = copy[key];
      if (typeof val === "object" && val !== null) {
        copy[key] = formatDatesInObject(val);
      } else if (typeof val === "string") {
        const isDateKey =
          key.toLowerCase().includes("date") ||
          key.toLowerCase().endsWith("_at");
        const isDatePattern =
          /^\d{4}-\d{2}-\d{2}$/.test(val) ||
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val);
        if (isDateKey || isDatePattern) {
          const d = dayjs(val);
          if (d.isValid()) {
            copy[key] = d.format("MM-DD-YYYY hh:mm A");
          }
        }
      }
    }
    return copy;
  };

  // Extract comparative changes list for detailed Modal
  const getDetailedDifferences = (oldVal, newVal) => {
    const diffs = [];
    const allKeys = new Set([
      ...Object.keys(oldVal || {}),
      ...Object.keys(newVal || {}),
    ]);
    allKeys.forEach((key) => {
      const oldK = oldVal ? oldVal[key] : undefined;
      const newK = newVal ? newVal[key] : undefined;
      if (JSON.stringify(oldK) !== JSON.stringify(newK)) {
        let fieldName = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        if (key === "selected_matric_ids") {
          fieldName = "Selected Matric IDs";
        } else if (key === "child_packages") {
          fieldName = "Child Packages";
        }
        let oldDisplay;
        let newDisplay;
        if (key === "source_package_tag") {
          oldDisplay = getTagCode(oldK);
          newDisplay = getTagCode(newK);
        } else if (key === "selected_matric_ids" || key === "child_packages") {
          oldDisplay = parseSpecialKeys(key, oldK);
          newDisplay = parseSpecialKeys(key, newK);
        } else {
          oldDisplay = formatDateValue(oldK, key);
          newDisplay = formatDateValue(newK, key);
        }
        diffs.push({
          field: fieldName,
          old: oldDisplay,
          new: newDisplay,
        });
      }
    });
    return diffs;
  };

  // Menu Handlers
  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDetails = () => {
    handleMenuClose();
    setDetailsModalOpen(true);
  };

  // CSV Exporter (queries full matching dataset from server)
  const handleDownloadCSV = async () => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        pageSize: 10000, // retrieve all records matching filters
        sortBy: sortBy,
        sortOrder: sortOrder.toUpperCase(),
      };

      if (search.trim() !== "") {
        params.search = search;
      }

      if (harvestDate) {
        params.harvestDate = harvestDate.format("YYYY-MM-DD");
      }

      const res = await httpService.get("audit-logs", params);
      const dataObj = res?.data || res || {};
      const allFilteredLogs = dataObj.data || [];

      if (allFilteredLogs.length === 0) {
        toast.info("No records to download");
        return;
      }

      const headers = [
        "Timestamp",
        "Harvest Date",
        "Source Package Tag",
        "Action Type",
        "Old Value",
        "New Value",
        "User",
      ];

      const csvRows = [];
      csvRows.push(headers.join(","));

      allFilteredLogs.forEach((log) => {
        const oldVal = log.old_value;
        const newVal = log.new_value;

        const timestamp = dayjs(log.created_at).format("YYYY-MM-DD HH:mm A");
        const hDateVal = newVal?.harvest_date || oldVal?.harvest_date;
        const harvestDateStr = hDateVal
          ? dayjs(hDateVal).format("MM-DD-YYYY")
          : "-";
        const tagCode = getTagCode(
          newVal?.source_package_tag || oldVal?.source_package_tag,
        );
        const actionType = getActionType(log.action);

        const oldTexts = [];
        const newTexts = [];

        const act = String(log.action || "").toUpperCase();
        const isMoveAction = act === "MOVED" || act === "MOVE";

        const oldValSource = oldVal || {};
        const newValSource = newVal || {};

        const allKeys = new Set([
          ...Object.keys(oldValSource),
          ...Object.keys(newValSource),
        ]);

        allKeys.forEach((key) => {
          if (key === "id" || key === "created_at" || key === "updated_at")
            return;

          if (key === "move_to" || key === "moved_at") {
            if (isMoveAction) {
              const displayVal =
                oldValSource[key] !== undefined && oldValSource[key] !== null
                  ? oldValSource[key]
                  : newValSource[key];
              if (
                displayVal !== undefined &&
                displayVal !== null &&
                displayVal !== ""
              ) {
                const oldD = formatDateValue(displayVal, key);
                oldTexts.push(`${key}: ${oldD}`);
              }
              return;
            }
          }

          if (oldVal) {
            const oldK = oldVal[key];
            const newK = newVal ? newVal[key] : undefined;
            if (
              JSON.stringify(oldK) !== JSON.stringify(newK) &&
              (oldK !== undefined || newK !== undefined)
            ) {
              let oldD;
              if (key === "selected_matric_ids" || key === "child_packages") {
                oldD = parseSpecialKeys(key, oldK);
              } else if (
                key === "source_package_tag" ||
                key === "child_package_tag"
              ) {
                oldD = getTagCode(oldK);
              } else {
                oldD = formatDateValue(oldK, key);
              }
              oldTexts.push(`${key}: ${oldD}`);
            }
          }

          if (newVal) {
            const oldK = oldVal ? oldVal[key] : undefined;
            const newK = newVal[key];
            if (
              JSON.stringify(oldK) !== JSON.stringify(newK) &&
              (oldK !== undefined || newK !== undefined)
            ) {
              let newD;
              if (key === "selected_matric_ids" || key === "child_packages") {
                newD = parseSpecialKeys(key, newK);
              } else if (
                key === "source_package_tag" ||
                key === "child_package_tag"
              ) {
                newD = getTagCode(newK);
              } else {
                newD = formatDateValue(newK, key);
              }
              newTexts.push(`${key}: ${newD}`);
            }
          }
        });
        const oldStr = oldTexts.length > 0 ? oldTexts.join(" | ") : "-";
        const newStr = newTexts.length > 0 ? newTexts.join(" | ") : "-";
        const user = log.performer_name || "System";

        const rowValues = [
          `"${timestamp}"`,
          `"${harvestDateStr}"`,
          `"${tagCode}"`,
          `"${actionType}"`,
          `"${oldStr.replace(/"/g, '""')}"`,
          `"${newStr.replace(/"/g, '""')}"`,
          `"${user}"`,
        ];
        csvRows.push(rowValues.join(","));
      });

      const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `post_harvest_audit_logs_${dayjs().format("YYYYMMDD_HHmmss")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Error downloading CSV:", e);
      toast.error("Failed to download CSV");
    } finally {
      setLoading(false);
    }
  };

  // DataGrid Columns Definition
  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row)}>
          <GridMoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const hDate =
          params.row.new_value?.harvest_date ||
          params.row.old_value?.harvest_date;
        return hDate ? dayjs(hDate).format("MM-DD-YYYY") : "-";
      },
    },
    {
      field: "source_package_tag",
      headerName: "Source Package Tag",
      flex: 1.5,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const tag =
          params.row.new_value?.source_package_tag ||
          params.row.old_value?.source_package_tag;
        return getTagCode(tag);
      },
    },
    {
      field: "table_name",
      headerName: "Table Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => getActionType(params.row.table_name),
    },
    {
      field: "action_type",
      headerName: "Action Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => getActionType(params.row.action),
    },
    {
      field: "created_at",
      headerName: "Action Date",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const d = params.value;
        return d ? dayjs(d).format("MM-DD-YYYY hh:mm A") : "-";
      },
    },
    {
      field: "old_value",
      headerName: "Old Value",
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) =>
        renderValues(
          params.row.old_value,
          params.row.new_value,
          params.row.action,
          true,
        ),
    },
    {
      field: "new_value",
      headerName: "New Value",
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) =>
        renderValues(
          params.row.new_value,
          params.row.old_value,
          params.row.action,
          false,
        ),
    },
    {
      field: "performer_name",
      headerName: "User",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => params.value || "System",
    },
  ];

  const gridPinnedColumns = React.useMemo(
    () => ({
      left: ["action"],
    }),
    [],
  );

  if (!isAuthorized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "75vh",
          width: "100%",
          padding: "20px",
        }}
      >
        <div
          className="modal_block login_modal_block"
          style={{
            position: "relative",
            top: "auto",
            left: "auto",
            transform: "none",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e0e0e0",
            width: "100%",
            maxWidth: "450px",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <div
            className="modal_header"
            style={{ borderBottom: "none", padding: "40px 40px 10px" }}
          >
            <div className="modal_title_wrapper">
              <div className="title_wrapper">
                <h1
                  className="login_title"
                  style={{
                    fontSize: "28px",
                    color: "#1e2eca",
                    fontWeight: "700",
                    margin: 0,
                  }}
                >
                  Audit Log Access
                </h1>
                <div
                  className="desc"
                  style={{
                    marginTop: "8px",
                    fontSize: "14px",
                    color: "#6c757d",
                  }}
                >
                  Please enter credentials to access the Audit Logs.
                </div>
              </div>
            </div>
          </div>
          <div className="modal_content" style={{ padding: "0 40px 40px" }}>
            <div className="login_inner">
              <div className="form_wrapper">
                <div className="login_form">
                  <div className="login_form_inner">
                    <form onSubmit={handleAuditLoginSubmit}>
                      <div
                        className={`form_field_wrapper ${emailErr ? "error" : ""}`}
                      >
                        <label style={{ color: emailErr ? "red" : "inherit" }}>
                          Email / Username
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: emailErr
                                ? "1px solid red !important"
                                : "1px solid #ced4da !important",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: emailErr
                                ? "red !important"
                                : "#b3b3b3 !important",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: emailErr
                                ? "red !important"
                                : "#00A272 !important",
                              borderWidth: "1px !important",
                            },
                          }}
                          type="text"
                          placeholder="Enter Username"
                          variant="outlined"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          onFocus={() => setEmailErr("")}
                        />
                        {emailErr && (
                          <FormHelperText
                            id="component-error-text"
                            style={{ color: "red" }}
                          >
                            {emailErr}
                          </FormHelperText>
                        )}
                      </div>

                      <div
                        className={`form_field_wrapper ${passwordErr ? "error" : ""}`}
                        style={{ marginTop: "16px" }}
                      >
                        <label
                          style={{ color: passwordErr ? "red" : "inherit" }}
                        >
                          Password<span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: passwordErr
                                ? "1px solid red !important"
                                : "1px solid #ced4da !important",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: passwordErr
                                ? "red !important"
                                : "#b3b3b3 !important",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: passwordErr
                                ? "red !important"
                                : "#00A272 !important",
                              borderWidth: "1px !important",
                            },
                          }}
                          type={showPass ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPass(!showPass)}
                                  edge="end"
                                >
                                  {showPass ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Enter Password"
                          variant="outlined"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          onFocus={() => setPasswordErr("")}
                        />
                        {passwordErr && (
                          <FormHelperText
                            id="component-error-text"
                            style={{ color: "red" }}
                          >
                            {passwordErr}
                          </FormHelperText>
                        )}
                      </div>

                      <div
                        className="remember-me-wrap"
                        style={{
                          marginTop: "12px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              sx={{
                                color: "#ced4da",
                                "&.Mui-checked": {
                                  color: "#00A272",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              style={{
                                fontSize: "14px",
                                color: "#495057",
                                fontFamily: "Outfit, Inter, sans-serif",
                              }}
                            >
                              Keep me logged in
                            </Typography>
                          }
                        />
                      </div>

                      <Button
                        type="submit"
                        className="btn btn-primary"
                        style={{
                          width: "100%",
                          marginTop: "24px",
                          backgroundColor: "#00A272",
                          borderColor: "#00A272",
                          color: "#fff",
                          padding: "10px 0",
                          borderRadius: "8px",
                          fontWeight: "bold",
                        }}
                      >
                        SIGN IN
                      </Button>

                      {/* <div style={{ marginTop: "16px", textAlign: "center", fontSize: "12px", color: "#858e96" }}>
                        (Static Credentials: <strong>AuditAdmin</strong> / <strong>AuditAdmin@123</strong>)
                      </div> */}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && <FullScreenLoader />}

      <div className="audit-logs-container">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Post Harvest Audit Logs</h1>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="table-filter-wrap d-flex justify-content-between align-items-center">
          <div className="d-flex flex-row align-items-center gap-3">
            <div
              className="header_search_bar common_content"
              style={{ margin: 0 }}
            >
              <div className="input-group input_group">
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Source Package ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={harvestDate}
                onChange={(newValue) => setHarvestDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "Harvest Date",
                    sx: {
                      width: 200,
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "& .MuiInputBase-root": {
                        borderRadius: "0.375rem",
                        height: "38px",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>

          <div className="button-wrap" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{
                backgroundColor: "#0c2eca",
                borderColor: "#0c2eca",
                padding: "8px 24px",
              }}
              onClick={handleDownloadCSV}
            >
              DOWNLOAD
            </button>
          </div>
        </div>

        {/* Table wrapper */}
        <div
          className="common_content frozen-table-wrapper"
          style={{ width: "100%", marginTop: 20 }}
        >
          {/* Fake frozen header overlay */}
          <div className="fake-frozen-header">
            <div className="fake-header-cell" style={{ width: 80, height: 56 }}>
              Action
            </div>
          </div>

          <DataGrid
            rows={logs}
            columns={columns}
            getRowId={(row) => row.id}
            autoHeight
            getRowHeight={() => "auto"}
            columnHeaderHeight={56}
            disableRowSelectionOnClick
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            pageSizeOptions={[15, 50, 100]}
            columnBuffer={columns.length}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            onSortModelChange={(model) => {
              if (model.length > 0) {
                setSortBy(model[0].field);
                setSortOrder(model[0].sort);
              } else {
                setSortBy("created_at");
                setSortOrder("desc");
              }
            }}
            showCellVerticalBorder
            showColumnVerticalBorder
            pinnedColumns={gridPinnedColumns}
            sx={{
              border: "1px solid #b3b3b3",
              [`& .${gridClasses.cell}`]: { py: 1.5 },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#e5e0f8 !important",
                color: "#1e1e1e",
                fontWeight: "bold",
                fontSize: "14px",
                fontFamily: "Outfit, Inter, sans-serif",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#e5e0f8 !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
              },
            }}
          />
        </div>
      </div>

      {/* Action Popover Menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleOpenDetails} style={{ gap: "8px" }}>
          <span>👁️</span> View Details
        </MenuItem>
      </Popover>

      {/* Details Comparative Modal */}
      <Modal open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)}>
        <div
          className="modal_block"
          style={{ maxWidth: "700px", width: "90%" }}
        >
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Audit Log Details</h2>
            </div>
            <div className="cancel-btn-wrap" style={{ cursor: "pointer" }}>
              <RxCross1 onClick={() => setDetailsModalOpen(false)} />
            </div>
          </div>
          <div
            className="modal_content"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {selectedRow && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  gap={2}
                  bgcolor="#f8f9fa"
                  p={2}
                  borderRadius="4px"
                >
                  <Typography variant="body2">
                    <strong>Table Name:</strong> {selectedRow.table_name}
                  </Typography>
                  {/* <Typography variant="body2">
                    <strong>Record ID:</strong> {selectedRow.record_id}
                  </Typography> */}
                  <Typography variant="body2">
                    <strong>Action Type:</strong>{" "}
                    {getActionType(selectedRow.action)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Source Package Tag:</strong>{" "}
                    {selectedRow.new_value?.source_package_tag ||
                      selectedRow.old_value?.source_package_tag}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Performed By:</strong>{" "}
                    {selectedRow.performer_name || "System"}
                    {/* {selectedRow.performer_email || "N/A"}) */}
                  </Typography>
                  {/* <Typography variant="body2" style={{ gridColumn: "span 2" }}>
                    <strong>Timestamp:</strong>{" "}
                    {dayjs(selectedRow.created_at).format("YYYY-MM-DD HH:mm A")}
                  </Typography> */}
                </Box>

                <Typography
                  variant="h6"
                  style={{
                    marginTop: "10px",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Differences
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: "#f1f3f5" }}>
                        <TableCell>
                          <strong>Field Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Old Value</strong>
                        </TableCell>
                        <TableCell>
                          <strong>New Value</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDetailedDifferences(
                        selectedRow.old_value,
                        selectedRow.new_value,
                      ).map((diff, idx) => (
                        <TableRow key={idx}>
                          <TableCell style={{ wordBreak: "break-word" }}>
                            <strong>{diff.field}</strong>
                          </TableCell>
                          <TableCell
                            style={{
                              color: "#d9534f",
                              wordBreak: "break-word",
                            }}
                          >
                            {diff.old}
                          </TableCell>
                          <TableCell
                            style={{
                              color: "#5cb85c",
                              wordBreak: "break-word",
                            }}
                          >
                            {diff.new}
                          </TableCell>
                        </TableRow>
                      ))}
                      {getDetailedDifferences(
                        selectedRow.old_value,
                        selectedRow.new_value,
                      ).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No changes recorded or new record created.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  gap={2}
                  style={{ marginTop: "10px" }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: "bold", marginBottom: "4px" }}
                    >
                      Raw Old Value:
                    </Typography>
                    <pre
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        overflow: "auto",
                        maxHeight: "150px",
                      }}
                    >
                      {selectedRow.old_value
                        ? JSON.stringify(
                            formatDatesInObject(selectedRow.old_value),
                            null,
                            2,
                          )
                        : "null"}
                    </pre>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: "bold", marginBottom: "4px" }}
                    >
                      Raw New Value:
                    </Typography>
                    <pre
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        overflow: "auto",
                        maxHeight: "150px",
                      }}
                    >
                      {selectedRow.new_value
                        ? JSON.stringify(
                            formatDatesInObject(selectedRow.new_value),
                            null,
                            2,
                          )
                        : "null"}
                    </pre>
                  </Box>
                </Box>
              </Box>
            )}
          </div>
          <div
            className="modal_footer"
            style={{ borderTop: "1px solid #E0E0E0", padding: "15px 20px" }}
          >
            <button
              onClick={() => setDetailsModalOpen(false)}
              className="btn btn-border"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AuditLogs;
